import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, CheckCircle, Plus, Trash2, Save, Upload, Award, User, Building2, Edit2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const generateCompanySignatureHtml = (contact, company, social, sigSettings) => {
  const color = sigSettings.company_color || '#D4AF37';
  const logoW = Math.min(sigSettings.logo_width || 120, 200);
  const awards = sigSettings.awards || [];

  const socialIcons = [
    { key: 'linkedin', url: social?.linkedin, label: 'LinkedIn' },
    { key: 'twitter', url: social?.twitter, label: 'X' },
    { key: 'instagram', url: social?.instagram, label: 'Instagram' },
    { key: 'facebook', url: social?.facebook, label: 'Facebook' },
  ].filter(s => s.url);

  const phoneLine = (contact?.phone || contact?.mobile)
    ? `<tr><td style="padding:1px 8px 1px 0;color:#999;">Tel</td><td style="padding:1px 0;">${contact?.phone ? `<a href="tel:${contact.phone.replace(/\\s/g,'')}" style="color:#333;text-decoration:none;">${contact.phone}</a>` : ''}${(contact?.phone && contact?.mobile) ? `<span style="color:#ccc;margin:0 8px;">|</span>` : ''}${contact?.mobile ? `<a href="tel:${contact.mobile.replace(/\\s/g,'')}" style="color:#333;text-decoration:none;">${contact.mobile}</a>` : ''}</td></tr>`
    : '';

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333;max-width:600px;"><tbody>
<tr><td style="padding:0 0 12px 0;">
  <table cellpadding="0" cellspacing="0" border="0"><tbody><tr>
    ${sigSettings.logo_url ? `<td style="padding-right:16px;vertical-align:top;"><img src="${sigSettings.logo_url}" alt="${company?.company_name || 'Black Star PI'}" width="${logoW}" style="max-width:${logoW}px;height:auto;display:block;" /></td>` : ''}
    <td style="vertical-align:top;">
      <p style="margin:0;font-size:16px;font-weight:700;color:#000;letter-spacing:0.5px;">${company?.company_name || 'Black Star PI'}</p>
      ${sigSettings.tagline ? `<p style="margin:2px 0 0;font-size:11px;color:${color};letter-spacing:1.5px;text-transform:uppercase;">${sigSettings.tagline}</p>` : ''}
    </td>
  </tr></tbody></table>
</td></tr>
<tr><td style="border-top:2px solid ${color};padding:12px 0 0 0;">
  <table cellpadding="0" cellspacing="0" border="0" style="font-size:12px;color:#555;"><tbody>
    ${phoneLine}
    ${contact?.email ? `<tr><td style="padding:1px 8px 1px 0;color:#999;">Email</td><td style="padding:1px 0;"><a href="mailto:${contact.email}" style="color:${color};text-decoration:none;">${contact.email}</a></td></tr>` : ''}
    ${contact?.website ? `<tr><td style="padding:1px 8px 1px 0;color:#999;">Web</td><td style="padding:1px 0;"><a href="https://${contact.website}" style="color:${color};text-decoration:none;">${contact.website}</a></td></tr>` : ''}
    ${company?.address_line1 ? `<tr><td style="padding:1px 8px 1px 0;color:#999;">Addr</td><td style="padding:1px 0;color:#555;">${company.address_line1}, ${company.address_line2}, ${company.address_postcode}</td></tr>` : ''}
  </tbody></table>
</td></tr>
${socialIcons.length > 0 ? `<tr><td style="padding:10px 0 0 0;">${socialIcons.map(s => `<a href="${s.url}" target="_blank" rel="noopener" style="color:${color};text-decoration:none;font-size:11px;margin-right:12px;">${s.label}</a>`).join('')}</td></tr>` : ''}
${awards.length > 0 ? `<tr><td style="padding:12px 0 0 0;"><table cellpadding="0" cellspacing="0" border="0"><tbody><tr>${awards.map(a => `<td style="padding-right:8px;">${a.link ? `<a href="${a.link}" target="_blank" rel="noopener">` : ''}<img src="${a.image_url}" alt="${a.name}" height="48" style="height:48px;width:auto;display:block;" title="${a.name}" />${a.link ? '</a>' : ''}</td>`).join('')}</tr></tbody></table></td></tr>` : ''}
${sigSettings.banner_text ? `<tr><td style="padding:10px 0 0 0;font-size:10px;color:#999;font-style:italic;">${sigSettings.banner_text}</td></tr>` : ''}
</tbody></table>`;
};

const generateTeamSignatureHtml = (member, contact, company, social, sigSettings) => {
  const color = sigSettings.company_color || '#D4AF37';
  const logoW = Math.min(sigSettings.logo_width || 120, 160);
  const awards = sigSettings.awards || [];

  const socialIcons = [
    { key: 'linkedin', url: member.linkedin || social?.linkedin, label: 'LinkedIn' },
    { key: 'twitter', url: social?.twitter, label: 'X' },
  ].filter(s => s.url);

  const phoneLine = (contact?.phone || contact?.mobile)
    ? `<tr><td colspan="2" style="padding:0;font-size:11px;">${contact?.phone ? `<span style="color:#999;">T</span> <a href="tel:${contact.phone.replace(/\\s/g,'')}" style="color:#333;text-decoration:none;">${contact.phone}</a>` : ''}${(contact?.phone && contact?.mobile) ? `<span style="color:#ccc;margin:0 6px;">|</span>` : ''}${contact?.mobile ? `<span style="color:#999;">M</span> <a href="tel:${contact.mobile.replace(/\\s/g,'')}" style="color:#333;text-decoration:none;">${contact.mobile}</a>` : ''}</td></tr>`
    : '';

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#333;max-width:600px;"><tbody>
<tr><td style="padding:0 0 12px 0;">
  <table cellpadding="0" cellspacing="0" border="0"><tbody><tr>
    ${member.image ? `<td style="padding-right:14px;vertical-align:top;"><img src="${member.image}" alt="${member.name}" width="64" height="64" style="width:64px;height:64px;border-radius:50%;object-fit:cover;display:block;" /></td>` : ''}
    <td style="vertical-align:top;">
      <p style="margin:0;font-size:15px;font-weight:700;color:#000;">${member.name}</p>
      <p style="margin:2px 0 0;font-size:12px;color:${color};font-weight:500;">${member.role}</p>
      <p style="margin:4px 0 0;font-size:11px;color:#777;">${member.email || ''}</p>
    </td>
  </tr></tbody></table>
</td></tr>
<tr><td style="border-top:2px solid ${color};padding:10px 0 0 0;">
  <table cellpadding="0" cellspacing="0" border="0"><tbody><tr>
    ${sigSettings.logo_url ? `<td style="padding-right:14px;vertical-align:middle;"><img src="${sigSettings.logo_url}" alt="${company?.company_name || 'Black Star PI'}" width="${logoW}" style="max-width:${logoW}px;height:auto;display:block;" /></td>` : ''}
    <td style="vertical-align:middle;">
      <p style="margin:0;font-size:12px;font-weight:600;color:#000;">${company?.company_name || 'Black Star PI'}</p>
      ${sigSettings.tagline ? `<p style="margin:1px 0 0;font-size:10px;color:${color};letter-spacing:1px;text-transform:uppercase;">${sigSettings.tagline}</p>` : ''}
      <table cellpadding="0" cellspacing="0" border="0" style="margin-top:4px;font-size:11px;color:#555;"><tbody>
        ${phoneLine}
        ${contact?.email ? `<tr><td style="padding:0 6px 0 0;color:#999;">E</td><td><a href="mailto:${contact.email}" style="color:${color};text-decoration:none;">${contact.email}</a></td></tr>` : ''}
        ${contact?.website ? `<tr><td style="padding:0 6px 0 0;color:#999;">W</td><td><a href="https://${contact.website}" style="color:${color};text-decoration:none;">${contact.website}</a></td></tr>` : ''}
      </tbody></table>
    </td>
  </tr></tbody></table>
</td></tr>
${socialIcons.length > 0 ? `<tr><td style="padding:8px 0 0 0;">${socialIcons.map(s => `<a href="${s.url}" target="_blank" rel="noopener" style="color:${color};text-decoration:none;font-size:11px;margin-right:12px;">${s.label}</a>`).join('')}</td></tr>` : ''}
${awards.length > 0 ? `<tr><td style="padding:10px 0 0 0;"><table cellpadding="0" cellspacing="0" border="0"><tbody><tr>${awards.map(a => `<td style="padding-right:6px;">${a.link ? `<a href="${a.link}" target="_blank" rel="noopener">` : ''}<img src="${a.image_url}" alt="${a.name}" height="40" style="height:40px;width:auto;display:block;" title="${a.name}" />${a.link ? '</a>' : ''}</td>`).join('')}</tr></tbody></table></td></tr>` : ''}
</tbody></table>`;
};

const SignaturePreview = ({ html, label, onEdit }) => {
  const [copied, setCopied] = useState(false);

  const copyHtml = async () => {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      const plainBlob = new Blob([html], { type: 'text/plain' });
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })
      ]);
      setCopied(true);
      toast.success(`${label} signature copied!`);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = html;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      toast.success(`${label} signature HTML copied!`);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
        <span className="text-white/70 text-sm font-medium">{label}</span>
        <div className="flex items-center gap-1">
          {onEdit && (
            <Button onClick={onEdit} variant="ghost" size="sm" className="h-8 text-xs text-white/50 hover:text-white" data-testid={`edit-sig-${label.toLowerCase().replace(/\s/g, '-')}`}>
              <Edit2 className="w-3 h-3 mr-1" /> Edit
            </Button>
          )}
          <Button onClick={copyHtml} variant="ghost" size="sm" className={`h-8 text-xs ${copied ? 'text-green-400' : 'text-[#D4AF37]'}`} data-testid={`copy-sig-${label.toLowerCase().replace(/\s/g, '-')}`}>
            {copied ? <><CheckCircle className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
          </Button>
        </div>
      </div>
      <div className="p-4 bg-white" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

export const SignaturesTab = ({ getStoredPassword }) => {
  const [sigSettings, setSigSettings] = useState({ logo_url: '', logo_width: 120, tagline: 'Acquire. Optimise. Multiply.', banner_text: '', awards: [], company_color: '#D4AF37', signature_members: [] });
  const [contact, setContact] = useState(null);
  const [company, setCompany] = useState(null);
  const [social, setSocial] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [awardUploading, setAwardUploading] = useState(false);
  const [newAward, setNewAward] = useState({ name: '', image_url: '', link: '' });
  const logoInputRef = useRef(null);
  const awardInputRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    passwordRef.current = getStoredPassword();
    const fetchAll = async () => {
      const pw = passwordRef.current;
      try {
        const [sigRes, contactRes, companyRes, socialRes, teamRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/signature-settings?password=${pw}`),
          fetch(`${API_URL}/api/public/contact-details`),
          fetch(`${API_URL}/api/public/company-info`),
          fetch(`${API_URL}/api/public/social-links`),
          fetch(`${API_URL}/api/admin/team?password=${pw}`)
        ]);
        if (sigRes.ok) setSigSettings(await sigRes.json());
        if (contactRes.ok) setContact(await contactRes.json());
        if (companyRes.ok) setCompany(await companyRes.json());
        if (socialRes.ok) setSocial(await socialRes.json());
        if (teamRes.ok) setTeamMembers((await teamRes.json()).filter(m => m.visible !== false));
      } catch (e) {
        console.error('Failed to load signature data');
      }
      setLoading(false);
    };
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save helper: saves given settings object to backend immediately
  const persistSettings = useCallback(async (settingsToSave) => {
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      await fetch(`${API_URL}/api/admin/signature-settings?password=${pw}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsToSave)
      });
    } catch (e) {
      console.error('Auto-save failed');
    }
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      const res = await fetch(`${API_URL}/api/admin/signature-settings?password=${pw}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sigSettings)
      });
      if (res.ok) toast.success('Signature settings saved');
      else toast.error('Failed to save');
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const uploadLogo = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      const res = await fetch(`${API_URL}/api/upload/logo?password=${pw}`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setSigSettings(prev => ({ ...prev, logo_url: data.image_url }));
        toast.success('Logo uploaded');
      }
    } catch (e) { toast.error('Upload failed'); }
    setUploading(false);
  };

  const uploadAwardImage = async (file) => {
    setAwardUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      const res = await fetch(`${API_URL}/api/upload/logo?password=${pw}`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setNewAward(prev => ({ ...prev, image_url: data.image_url }));
        toast.success('Award image uploaded');
      }
    } catch (e) { toast.error('Upload failed'); }
    setAwardUploading(false);
  };

  // Auto-save awards immediately when added
  const addAward = () => {
    if (!newAward.name || !newAward.image_url) { toast.error('Award name and image are required'); return; }
    const updated = {
      ...sigSettings,
      awards: [...(sigSettings.awards || []), { ...newAward, id: crypto.randomUUID(), order: (sigSettings.awards || []).length }]
    };
    setSigSettings(updated);
    persistSettings(updated);
    setNewAward({ name: '', image_url: '', link: '' });
    toast.success('Award badge added');
  };

  // Auto-save when award removed
  const removeAward = (id) => {
    const updated = { ...sigSettings, awards: (sigSettings.awards || []).filter(a => a.id !== id) };
    setSigSettings(updated);
    persistSettings(updated);
  };

  // ===== Signature-only members (stored in sigSettings, NOT in team_members) =====
  const [newMember, setNewMember] = useState({ name: '', role: '', email: '', image: '', linkedin: '' });
  const [showAddMember, setShowAddMember] = useState(false);
  const [savingMember, setSavingMember] = useState(false);
  const memberPhotoRef = useRef(null);
  const [memberPhotoUploading, setMemberPhotoUploading] = useState(false);

  const uploadMemberPhoto = async (file) => {
    setMemberPhotoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      const res = await fetch(`${API_URL}/api/upload/team-photo?password=${pw}`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setNewMember(prev => ({ ...prev, image: data.image_url }));
        toast.success('Photo uploaded');
      }
    } catch (e) { toast.error('Upload failed'); }
    setMemberPhotoUploading(false);
  };

  // Add member to signature_members (NOT to team_members)
  const addSignatureMember = () => {
    if (!newMember.name || !newMember.role || !newMember.email) { toast.error('Name, role and email are required'); return; }
    setSavingMember(true);
    const member = { ...newMember, id: crypto.randomUUID() };
    const updated = {
      ...sigSettings,
      signature_members: [...(sigSettings.signature_members || []), member]
    };
    setSigSettings(updated);
    persistSettings(updated);
    setNewMember({ name: '', role: '', email: '', image: '', linkedin: '' });
    setShowAddMember(false);
    setSavingMember(false);
    toast.success('Signature member added');
  };

  // Delete a signature-only member
  const deleteSignatureMember = (id) => {
    const updated = {
      ...sigSettings,
      signature_members: (sigSettings.signature_members || []).filter(m => m.id !== id)
    };
    setSigSettings(updated);
    persistSettings(updated);
    toast.success('Signature member removed');
  };

  // ===== Editing (team members use team API, signature members use sigSettings) =====
  const [editingMember, setEditingMember] = useState(null);
  const [editingType, setEditingType] = useState(null); // 'team' or 'sig'
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const editPhotoRef = useRef(null);
  const [editPhotoUploading, setEditPhotoUploading] = useState(false);

  const startEditMember = (member, type) => {
    setEditingMember(member.id);
    setEditingType(type);
    setEditForm({ name: member.name, role: member.role, email: member.email || '', image: member.image || '', linkedin: member.linkedin || '' });
  };

  const uploadEditPhoto = async (file) => {
    setEditPhotoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const pw = passwordRef.current || localStorage.getItem('adminPassword');
    try {
      const res = await fetch(`${API_URL}/api/upload/team-photo?password=${pw}`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setEditForm(prev => ({ ...prev, image: data.image_url }));
        toast.success('Photo uploaded');
      }
    } catch (e) { toast.error('Upload failed'); }
    setEditPhotoUploading(false);
  };

  const saveEditMember = async () => {
    if (!editForm.name || !editForm.role || !editForm.email) { toast.error('Name, role and email are required'); return; }
    setSavingEdit(true);

    if (editingType === 'team') {
      // Save to team API (auto-synced team member)
      const pw = passwordRef.current || localStorage.getItem('adminPassword');
      try {
        const member = teamMembers.find(m => m.id === editingMember);
        const res = await fetch(`${API_URL}/api/admin/team/${editingMember}?password=${pw}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...member, ...editForm })
        });
        if (res.ok) {
          toast.success('Team member updated');
          setEditingMember(null);
          const teamRes = await fetch(`${API_URL}/api/admin/team?password=${pw}`);
          if (teamRes.ok) setTeamMembers((await teamRes.json()).filter(m => m.visible !== false));
        } else { toast.error('Failed to update'); }
      } catch (e) { toast.error('Failed to update'); }
    } else {
      // Save to signature_members in sigSettings
      const updated = {
        ...sigSettings,
        signature_members: (sigSettings.signature_members || []).map(m =>
          m.id === editingMember ? { ...m, ...editForm } : m
        )
      };
      setSigSettings(updated);
      persistSettings(updated);
      setEditingMember(null);
      toast.success('Signature member updated');
    }

    setSavingEdit(false);
  };

  const brandingRef = useRef(null);

  if (loading) return <div className="text-center text-white/50 py-8">Loading...</div>;

  const companyHtml = generateCompanySignatureHtml(contact, company, social, sigSettings);
  const sigMembers = sigSettings.signature_members || [];
  const allSignatureMembers = [...teamMembers, ...sigMembers];

  const EditForm = ({ memberId }) => (
    <div className="bg-white/5 border border-[#D4AF37]/30 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-white text-sm font-medium">Editing: {editForm.name}</span>
        <button onClick={() => setEditingMember(null)} className="p-1 hover:bg-white/10 rounded text-white/40"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-5 gap-3 items-end">
        <div>
          <label className="text-white/50 text-xs block mb-1">Photo</label>
          <div className="flex items-center gap-2">
            {editForm.image ? (
              <img src={editForm.image} alt="" className="w-9 h-9 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><User className="w-4 h-4 text-white/30" /></div>
            )}
            <input ref={editPhotoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadEditPhoto(e.target.files[0])} />
            <button onClick={() => editPhotoRef.current?.click()} className="p-1.5 bg-white/5 border border-white/10 rounded hover:bg-white/10" disabled={editPhotoUploading}>
              <Upload className="w-3 h-3 text-white/50" />
            </button>
          </div>
        </div>
        <div>
          <label className="text-white/50 text-xs block mb-1">Name *</label>
          <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" />
        </div>
        <div>
          <label className="text-white/50 text-xs block mb-1">Role *</label>
          <Input value={editForm.role} onChange={(e) => setEditForm({...editForm, role: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" />
        </div>
        <div>
          <label className="text-white/50 text-xs block mb-1">Email *</label>
          <Input value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" />
        </div>
        <Button onClick={saveEditMember} className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-9" disabled={savingEdit} data-testid={`save-edit-member-${memberId}`}>
          <Save className="w-3 h-3 mr-1" /> {savingEdit ? 'Saving...' : 'Save'}
        </Button>
      </div>
      <div>
        <label className="text-white/50 text-xs block mb-1">LinkedIn (optional)</label>
        <Input value={editForm.linkedin} onChange={(e) => setEditForm({...editForm, linkedin: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg text-white">Email Signatures</h3>
          <p className="text-white/40 text-sm">Generate and copy branded email signatures for Outlook, Gmail, etc.</p>
        </div>
        <Button onClick={saveSettings} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]" disabled={saving} data-testid="save-signatures-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Signature Settings */}
      <div ref={brandingRef} className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
        <h4 className="text-white font-medium text-sm">Signature Branding</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Company Logo</label>
            <div className="flex items-center gap-2">
              <Input value={sigSettings.logo_url} onChange={(e) => setSigSettings({...sigSettings, logo_url: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="Logo URL..." />
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
              <button onClick={() => logoInputRef.current?.click()} className="p-2 bg-white/5 border border-white/10 rounded hover:bg-white/10 flex-shrink-0" disabled={uploading}>
                <Upload className="w-4 h-4 text-white/50" />
              </button>
            </div>
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Logo Width (px)</label>
            <Input type="number" value={sigSettings.logo_width} onChange={(e) => setSigSettings({...sigSettings, logo_width: Math.min(parseInt(e.target.value) || 120, 200)})} className="bg-white/5 border-white/10 text-white text-xs" min={40} max={200} />
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Brand Color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={sigSettings.company_color} onChange={(e) => setSigSettings({...sigSettings, company_color: e.target.value})} className="w-10 h-9 rounded border border-white/10 bg-transparent cursor-pointer" />
              <Input value={sigSettings.company_color} onChange={(e) => setSigSettings({...sigSettings, company_color: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs flex-1" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Tagline</label>
            <Input value={sigSettings.tagline} onChange={(e) => setSigSettings({...sigSettings, tagline: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="Acquire. Optimise. Multiply." />
          </div>
          <div>
            <label className="text-white/50 text-xs block mb-1.5">Banner Text (optional footer)</label>
            <Input value={sigSettings.banner_text} onChange={(e) => setSigSettings({...sigSettings, banner_text: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., FCA Authorised & Regulated" />
          </div>
        </div>
      </div>

      {/* Award Badges */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-[#D4AF37]" />
            <h4 className="text-white font-medium text-sm">Award Badges</h4>
          </div>
          <span className="text-white/30 text-xs">{(sigSettings.awards || []).length} badge{(sigSettings.awards || []).length !== 1 ? 's' : ''}</span>
        </div>

        {(sigSettings.awards || []).length > 0 && (
          <div className="flex flex-wrap gap-3">
            {(sigSettings.awards || []).map(award => (
              <div key={award.id} className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3 group">
                <img src={award.image_url} alt={award.name} className="h-10 w-auto object-contain" />
                <div>
                  <p className="text-white text-xs font-medium">{award.name}</p>
                  {award.link && <p className="text-white/30 text-[10px] truncate max-w-[120px]">{award.link}</p>}
                </div>
                <button onClick={() => removeAward(award.id)} className="p-1 hover:bg-red-500/20 rounded text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border border-dashed border-white/10 rounded-lg p-4">
          <div className="grid grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-white/50 text-xs block mb-1">Award Name *</label>
              <Input value={newAward.name} onChange={(e) => setNewAward({...newAward, name: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="e.g., Best AM Company" />
            </div>
            <div>
              <label className="text-white/50 text-xs block mb-1">Image *</label>
              <div className="flex items-center gap-1">
                <Input value={newAward.image_url} onChange={(e) => setNewAward({...newAward, image_url: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="URL..." />
                <input ref={awardInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadAwardImage(e.target.files[0])} />
                <button onClick={() => awardInputRef.current?.click()} className="p-2 bg-white/5 border border-white/10 rounded hover:bg-white/10 flex-shrink-0" disabled={awardUploading}>
                  <Upload className="w-3 h-3 text-white/50" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-white/50 text-xs block mb-1">Link (optional)</label>
              <Input value={newAward.link} onChange={(e) => setNewAward({...newAward, link: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs" placeholder="https://..." />
            </div>
            <Button onClick={addAward} className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-9" disabled={!newAward.name || !newAward.image_url} data-testid="add-award-btn">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      {/* Company Signature Preview */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-4 h-4 text-[#D4AF37]" />
          <h4 className="text-white font-medium text-sm">Company Signature</h4>
        </div>
        <SignaturePreview
          html={companyHtml}
          label="Company"
          onEdit={() => brandingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />
      </div>

      {/* Team Member Signatures */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#D4AF37]" />
            <h4 className="text-white font-medium text-sm">Team Signatures</h4>
            {allSignatureMembers.length > 0 && <span className="text-white/30 text-xs">({allSignatureMembers.length})</span>}
          </div>
          <Button
            onClick={() => setShowAddMember(!showAddMember)}
            variant={showAddMember ? "ghost" : "outline"}
            size="sm"
            className={showAddMember ? "text-white/60 h-8" : "border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8"}
            data-testid="add-team-member-sig-btn"
          >
            {showAddMember ? 'Cancel' : <><Plus className="w-3 h-3 mr-1" /> Add Member</>}
          </Button>
        </div>

        {/* Add Member Form — saves to signature_members, NOT team_members */}
        {showAddMember && (
          <div className="bg-white/5 border border-[#D4AF37]/20 rounded-lg p-4 mb-4 space-y-3">
            <div className="grid grid-cols-5 gap-3 items-end">
              <div>
                <label className="text-white/50 text-xs block mb-1">Photo</label>
                <div className="flex items-center gap-2">
                  {newMember.image ? (
                    <img src={newMember.image} alt="" className="w-9 h-9 rounded-full object-cover border border-white/10" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-white/30" />
                    </div>
                  )}
                  <input ref={memberPhotoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadMemberPhoto(e.target.files[0])} />
                  <button onClick={() => memberPhotoRef.current?.click()} className="p-1.5 bg-white/5 border border-white/10 rounded hover:bg-white/10" disabled={memberPhotoUploading}>
                    <Upload className="w-3 h-3 text-white/50" />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Name *</label>
                <Input value={newMember.name} onChange={(e) => setNewMember({...newMember, name: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" placeholder="Full name" />
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Role *</label>
                <Input value={newMember.role} onChange={(e) => setNewMember({...newMember, role: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" placeholder="Job title" />
              </div>
              <div>
                <label className="text-white/50 text-xs block mb-1">Email *</label>
                <Input value={newMember.email} onChange={(e) => setNewMember({...newMember, email: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" placeholder="name@black-starpi.com" />
              </div>
              <Button onClick={addSignatureMember} className="bg-[#D4AF37] text-black hover:bg-[#B8941E] h-9" disabled={savingMember || !newMember.name || !newMember.role || !newMember.email} data-testid="save-new-member-btn">
                {savingMember ? 'Adding...' : 'Add'}
              </Button>
            </div>
            <div>
              <label className="text-white/50 text-xs block mb-1">LinkedIn (optional)</label>
              <Input value={newMember.linkedin} onChange={(e) => setNewMember({...newMember, linkedin: e.target.value})} className="bg-white/5 border-white/10 text-white text-xs h-9" placeholder="https://linkedin.com/in/..." />
            </div>
          </div>
        )}

        {allSignatureMembers.length === 0 ? (
          <div className="bg-white/5 border border-dashed border-white/10 rounded-lg p-8 text-center">
            <User className="w-8 h-8 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 text-sm">No team members yet. Add team members in the Team tab or click "Add Member" for signature-only members.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Team members (auto-synced from Team tab) */}
            {teamMembers.map(member => (
              <div key={`team-${member.id}`}>
                {editingMember === member.id ? (
                  <EditForm memberId={member.id} />
                ) : (
                  <SignaturePreview
                    html={generateTeamSignatureHtml(member, contact, company, social, sigSettings)}
                    label={member.name}
                    onEdit={() => startEditMember(member, 'team')}
                  />
                )}
              </div>
            ))}

            {/* Signature-only members */}
            {sigMembers.map(member => (
              <div key={`sig-${member.id}`}>
                {editingMember === member.id ? (
                  <EditForm memberId={member.id} />
                ) : (
                  <div className="relative">
                    <SignaturePreview
                      html={generateTeamSignatureHtml(member, contact, company, social, sigSettings)}
                      label={member.name}
                      onEdit={() => startEditMember(member, 'sig')}
                    />
                    <button
                      onClick={() => deleteSignatureMember(member.id)}
                      className="absolute top-2 right-28 p-1 hover:bg-red-500/20 rounded text-red-400/60 hover:text-red-400 transition-colors"
                      data-testid={`delete-sig-member-${member.id}`}
                      title="Remove signature member"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
